import type { TeacherPageView } from "~/business-areas/teacher/teacher.model";
import { COURSE_RATING_MINIMUM_DISPLAY_THRESHOLD } from "~/pages/courses/[slug]/_includes/composables/course-page.data";

export async function useTeacherPageData(slug: string) {
  const { t } = useI18n();
  const cachedData = useTeacherCache().getBySlug(slug);

  const { data: teacher } = await useFetch(`/api/teachers/${slug}`, {
    deep: false,
    /*
     Lazy behaviour only if course has been previously cached from course any card list
     If not, course page view request is executed before route navigation and we can ensure
     that we don't throw 404 error in case of empty cache but existing course
    */
    lazy: !!cachedData,
    default: () => cachedData,
    transform(data): TeacherPageView | null {
      // `data` is typed as a union with the `/api/teachers/top` list route
      // because the URL is a dynamic string; the array case never happens here.
      if (!data || Array.isArray(data)) return null;

      return {
        ...data,
        _links: {
          teacherPage: {
            name: "trainers-slug",
            params: {
              slug: data.slug!,
            },
          },
        },
        mainTag: data.tags[0],
        courses: data.courses.map((course) => {
          const ratings = course.ratings[0];

          return {
            ...course,
            ratings:
              ratings.avg !== null &&
              ratings.count > 0 &&
              ratings.avg >= COURSE_RATING_MINIMUM_DISPLAY_THRESHOLD
                ? {
                    avg: ratings.avg,
                    count: ratings.count,
                  }
                : null,
          };
        }),
      };
    },
  });

  if (!teacher.value) {
    throw createError({
      statusCode: 404,
      statusMessage: t("app.errors.page_not_found"),
      fatal: true,
    });
  }

  return {
    teacher: teacher as Ref<TeacherPageView>,
  };
}
